import { Component, OnDestroy, OnInit, WritableSignal, computed, inject, signal } from '@angular/core';
import { BehaviorSubject, ReplaySubject, Subscription, skip } from 'rxjs';
import {
  Application,
  EThemeName,
  FeComponentLibraryModule,
  SidebarConfig,
  SidebarItem,
  ThemeService,
} from '@wind-log_library/fe-component-library';
import { NAVIGATION_ICONS } from 'src/app/modules/shared/pipes/icons/navigation-icons.constant';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService } from '@services/navigation/navigation.service';
import { environment } from '../../../environments/environment';
import { VersionService } from '@services/version-service/version.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  imports: [FeComponentLibraryModule],
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  showDropdown = true;
  application: Application = Application.PANORAMA;

  theme$ = new BehaviorSubject('');
  sidebarConfig: SidebarConfig | undefined;

  env = environment;

  redirectToRoute = (routeModule: string, e: any) => {
    this.navigationService.navigateToModule([routeModule], e);
  };

  private translateSubscription: Subscription = new Subscription();
  private unsubscriber = new ReplaySubject();
  private themeSubscription: Subscription | undefined;
  private navigationService = inject(NavigationService);
  private translate = inject(TranslateService);
  private themeService = inject(ThemeService);
  private versionService = inject(VersionService);
  sidebarItems = computed<WritableSignal<SidebarItem[] | undefined>>(() => signal(this.parseSidebarItems()));

  private sidebarConfigRAW = {
    bigScreenWidth: 1550,
    sidebarExpandedWidth: 250,
    sidebarCollapsedWidth: 64,
    collapseLabel: 'SIDE_NAV.COLLAPSE_SIDEBAR',
  };

  private sidebarItemsRAW: SidebarItem[] = [
    {
      route: 'monitor',
      module: 'module_monitor_resourcepanorama',
      icon: 'nav-monitor',
      title: 'SIDE_NAV.MONITOR',
    },
    {
      route: 'reports',
      module: 'module_reports_resourcepanorama',
      icon: 'nav-report',
      title: 'SIDE_NAV.REPORTS',
    },
  ];
  public versionInfo: string;
  constructor() {
    this.theme$ = this.themeService.getTheme();
  }
  ngOnInit(): void {
    this.sidebarConfig = this.parseSidebarConfig();
    this.translateSubscription = this.translate.onLangChange.subscribe(() => {
      this.sidebarConfig = this.parseSidebarConfig();
      this.sidebarItems().set(this.parseSidebarItems());
    });

    this.themeSubscription = this.themeService
      .getTheme()
      .pipe(skip(1))
      .subscribe(() => {
        this.theme$ = this.themeService.getTheme();
        this.sidebarItems().set(this.parseSidebarItems());
      });
    this.versionInfo = this.versionService.getFormattedVersion();
  }

  parseSidebarItems(): SidebarItem[] {
    const currentTheme = this.themeService.getTheme().value;
    const NAVIGATION_ICONS_MAP: any = {};
    for (const icon of NAVIGATION_ICONS) {
      NAVIGATION_ICONS_MAP[icon.key] = icon;
    }

    return this.sidebarItemsRAW.map(item => {
      const icon = NAVIGATION_ICONS_MAP[item.icon];
      const normalize = (str: string) => str.replace(/\/$/, '');
      const isActive = normalize(item.route) === normalize(this.navigationService.moduleIdSig());
      return {
        ...item,
        icon: currentTheme === 'dark' ? icon[EThemeName.DARK] : icon[EThemeName.LIGHT],
        title: this.translate.instant(item.title),
        showModule: this.showModule(item.module),
        isLocked: this.isLocked([item.module]),
        isActive: isActive,
        href: `${item.route}/`,
      };
    });
  }

  parseSidebarConfig(): SidebarConfig {
    const parsedConfig: any = {
      ...this.sidebarConfigRAW,
      infoItems: [
        {
          title: this.translate.instant('SIDE_NAV.GPM_HELP_CENTER'),
          route: 'mailto:RPSupport@dnv.com',
          icon: 'far fa-question-circle',
          notShowTooltip: true,
        },
        {
          title: this.translate.instant('SIDE_NAV.APP_VERSION', { version: this.versionInfo }),
          icon: `${this.env.shared_server}/general/main-interface/icon-info-lightgrey.svg`,
        },
      ],
      collapseLabel: this.translate.instant(this.sidebarConfigRAW.collapseLabel),
    };

    return parsedConfig;
  }

  showModule(role: string) {
    return this.navigationService.showModule(role);
  }

  isLocked(roles: string[]): boolean {
    return this.navigationService.isLocked(roles);
  }

  ngOnDestroy(): void {
    this.unsubscriber.next(true);
    this.unsubscriber.complete();
    if (this.translateSubscription) {
      this.translateSubscription.unsubscribe();
    }
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
  }
}
